import type { GameState, MaskedGameState, Move } from '@completto/shared';
import {
  applyMove,
  createBotGame,
  createRng,
  decideBotMove,
  forceAutoPass,
  maskGameState,
} from '@completto/shared';
import { log } from '../lib/logger.ts';
import type { GameOrchestrator } from './orchestrator.ts';

const VIEWER_ID = 'me';
const BOT_ID = 'bot';
const BOT_NICKNAME = 'AI';
const BOT_ACTION_DELAY_MS = 600;

/**
 * 사람(viewer='me') vs 봇('bot') 1:1 대결.
 *
 * 외부로는 MaskedGameState만 노출 — 봇의 비공개 lane은 자동 마스킹됨.
 * 사용자가 sendMove로 자기 턴을 마치면, 봇 턴 동안 자동으로 봇의 수를 순차 적용.
 */
export class BotMatchService implements GameOrchestrator {
  readonly myPlayerId = VIEWER_ID;
  private state: GameState;
  private listeners = new Set<(state: MaskedGameState) => void>();
  private botTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(nickname: string) {
    const seed = Math.floor(Math.random() * 1_000_000) + 1;
    log.info('bot-match.create', { seed, nickname });
    this.state = createBotGame(nickname, BOT_NICKNAME, createRng(seed));
  }

  start(): Promise<void> {
    return Promise.resolve();
  }

  async sendMove(move: Move): Promise<void> {
    try {
      const result = applyMove(this.state, move);
      this.state = result.state;
      log.info('bot-match.player-move', { move: move.type, bonusEarned: result.bonusEarned });
      this.notify();
      this.scheduleBotIfNeeded();
    } catch (err) {
      log.error('bot-match.move-rejected', err, { move: move.type });
      throw err;
    }
  }

  subscribe(fn: (state: MaskedGameState) => void): () => void {
    this.listeners.add(fn);
    fn(this.toMasked());
    return () => {
      this.listeners.delete(fn);
    };
  }

  getState(): MaskedGameState {
    return this.toMasked();
  }

  reset(): void {
    this.cancelBotTimer();
    const nickname = this.state.players[0]?.nickname ?? '나';
    const seed = Math.floor(Math.random() * 1_000_000) + 1;
    log.info('bot-match.reset', { seed });
    this.state = createBotGame(nickname, BOT_NICKNAME, createRng(seed));
    this.notify();
  }

  forceFinish(): void {
    this.cancelBotTimer();
    const me = this.state.players[0];
    if (!me) return;
    log.info('bot-match.cheat-finish', { playerId: me.id });
    this.state = {
      ...this.state,
      status: 'FINISHED',
      winnerId: me.id,
      metrics: { ...this.state.metrics, finishedAt: Date.now() },
    };
    this.notify();
  }

  dispose(): void {
    this.disposed = true;
    this.cancelBotTimer();
    this.listeners.clear();
  }

  private scheduleBotIfNeeded(): void {
    if (this.disposed) return;
    if (this.state.status !== 'IN_PROGRESS') return;
    const current = this.state.players[this.state.currentPlayerIdx];
    if (!current || current.id !== BOT_ID) return;

    this.cancelBotTimer();
    this.botTimer = setTimeout(() => {
      this.botTimer = null;
      this.runBotStep();
    }, BOT_ACTION_DELAY_MS);
  }

  private runBotStep(): void {
    if (this.disposed) return;
    if (this.state.status !== 'IN_PROGRESS') return;
    const current = this.state.players[this.state.currentPlayerIdx];
    if (!current || current.id !== BOT_ID) return;

    try {
      const move = decideBotMove(this.state, BOT_ID);
      if (move === null) {
        const { state: next, reason } = forceAutoPass(this.state);
        this.state = next;
        log.info('bot-match.bot-pass', { reason });
      } else {
        const result = applyMove(this.state, move);
        this.state = result.state;
        log.info('bot-match.bot-move', { move: move.type, bonusEarned: result.bonusEarned });
      }
    } catch (err) {
      log.error('bot-match.bot-error', err);
      return;
    }
    this.notify();
    this.scheduleBotIfNeeded();
  }

  private cancelBotTimer(): void {
    if (this.botTimer !== null) {
      clearTimeout(this.botTimer);
      this.botTimer = null;
    }
  }

  private notify(): void {
    const masked = this.toMasked();
    for (const fn of this.listeners) fn(masked);
  }

  private toMasked(): MaskedGameState {
    return maskGameState(this.state, VIEWER_ID);
  }
}
